/**
 * Where the per-block phone icons go: which call block each merged cell on a grid belongs to, and
 * how big its icon may be. Shared by the counter grid and the officer grid (see CallPhoneOverlay).
 */
import { callIconDiameter, type CallBlock } from '@/utils/callBlocks';
import type { NoReliefMark } from '@/utils/noRelief';

export type CallIconRect = { officerId: string; row: number; startCol: number; x: number; y: number; w: number; h: number };

/**
 * How a grid's merged segments line up with call blocks: the counter grid's rows are counters, so
 * a block is found by officer + counter row + start slot; the officer grid's rows are officers, so
 * by officer + start slot (a run on one counter is one merged cell there too).
 */
export type CallGridView = 'counter' | 'officer';

const rectKey = (view: CallGridView, officerId: string, row: number, startCol: number) =>
  view === 'counter' ? `${officerId}|${row}|${startCol}` : `${officerId}|${startCol}`;

/** The call blocks that have room for an icon, with where it goes and how big (at most half the cell's height). */
export function callIcons(blocks: CallBlock[], rects: CallIconRect[], view: CallGridView = 'counter') {
  const byStart = new Map<string, CallIconRect>();
  for (const r of rects) byStart.set(rectKey(view, r.officerId, r.row, r.startCol), r);
  const out: Array<{ block: CallBlock; rect: CallIconRect; size: number }> = [];
  for (const block of blocks) {
    const rect = byStart.get(rectKey(view, block.officerId, block.counter, block.startSlot));
    if (!rect) continue;
    const size = callIconDiameter(rect.h);
    if (size === 0 || rect.w < size * 2 + 8) continue;
    out.push({ block, rect, size });
  }
  return out;
}

// ── NR buttons (see NoReliefCellOverlay): pinned to the bottom-right of the block a counter goes unmanned after.

export interface BlockRect { officerId: string; row: number; startCol?: number; endCol: number; x: number; y: number; w: number; h: number }

/** The NR button is one fixed size on every block, however tall or wide the block is — only which of the
 *  three shapes it takes depends on the room. */
const PILL_H = 12;
const PILL_W = 20;       // "NR" beside the name (a lone "R" is PILL_H wide)
const PILL_NARROW_W = 9; // one time slot: N over R, or the same width without text
const STACK_FONT = 7;
const STACK_H = 2 * STACK_FONT + 5;

/** The button height for a block `h` tall, or 0 when the block is too short to hold one. */
export function nrButtonHeight(blockH: number): number {
  return blockH >= PILL_H + 2 ? PILL_H : 0;
}

/** Which rect each mark sits on: the block ending the slot before the counter goes unmanned, of an officer on
 *  it then — on the counter's own row in the counter grid, on the officer's row in the officer grid. */
export function placeMarks(marks: NoReliefMark[], rects: BlockRect[], view: CallGridView) {
  const out: Array<{ mark: NoReliefMark; rect: BlockRect }> = [];
  for (const mark of marks) {
    const end = mark.entry.slot - 1;
    for (const rect of rects) {
      if (rect.endCol !== end || !mark.officers.includes(rect.officerId)) continue;
      if (view === 'counter' && rect.row !== mark.entry.counter) continue;
      out.push({ mark, rect });
    }
  }
  return out;
}

/** A pill needs this many of its own widths of block, so the officer's name still shows beside it. */
const PILL_ROOM = 3;
export interface NrFit { w: number; h: number; compact: boolean; /** compact only: the letters are stacked one per line */ stacked?: boolean; font?: number }

/** One time slot's width in a block, or undefined when the block does not say where it starts. */
export const blockCellW = (rect: { w: number; startCol?: number; endCol?: number }) =>
  rect.startCol === undefined || rect.endCol === undefined ? undefined : rect.w / (rect.endCol - rect.startCol + 1);

/** How the NR button fits a block: a pill `w`×`h` with its label beside the name; or, on a block too narrow for
 *  that, a `compact` pill only one time slot (`cellW`) wide with the letters stacked down it (N over R) — or, where
 *  the block is too short for the stack, the same pill without text; or null when nothing would fit. */
export function nrButtonFit(rect: { w: number; h: number }, labelChars: number, cellW?: number): NrFit | null {
  const h = nrButtonHeight(rect.h);
  if (h === 0) return null;
  const w = labelChars > 1 ? PILL_W : PILL_H;
  if (rect.w >= w * PILL_ROOM) return { w, h, compact: false };
  const narrow = cellW !== undefined && cellW - 2 < PILL_NARROW_W ? Math.max(4, Math.round(cellW - 2)) : PILL_NARROW_W;
  if (rect.w < narrow + 2) return null;
  const stackH = labelChars > 1 ? STACK_H : PILL_H;
  return rect.h - 2 >= stackH && narrow >= 6
    ? { w: narrow, h: stackH, compact: true, stacked: true, font: STACK_FONT }
    : { w: narrow, h, compact: true };
}

/** Room (px) each block with an NR button keeps free at its right edge, keyed `officer|row|startCol`, so the
 *  block's text is laid out clear of the button instead of under it. */
export function nrRightInsets(marks: NoReliefMark[], rects: BlockRect[], view: CallGridView): Map<string, number> {
  const out = new Map<string, number>();
  for (const { mark, rect } of placeMarks(marks, rects, view)) {
    if (rect.startCol === undefined) continue;
    const fit = nrButtonFit(rect, mark.state === 'relief' ? 1 : 2, blockCellW(rect));
    if (fit) out.set(`${rect.officerId}|${rect.row}|${rect.startCol}`, fit.w + 3);
  }
  return out;
}
